import { router, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Checkbox } from '@/shared/ui/checkbox'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { adminLoggerPath, adminLoggersPath } from '@/shared/lib/routes'
import type { WebAdminLogger } from '@/shared/types/web'

interface Props {
  logger: WebAdminLogger | null
  isEdit: boolean
  [key: string]: unknown
}

interface FormErrors {
  [key: string]: string[] | undefined
}

const FieldError = ({ errors, field }: { errors: FormErrors; field: string }) => {
  const messages = errors[field]
  if (!messages?.length) return null
  return <p className="text-sm text-destructive mt-1">{messages[0]}</p>
}

const LoggerForm = () => {
  const { logger, isEdit } = usePage<Props>().props

  useBreadcrumbs(
    isEdit
      ? [
          { label: t('nav.administration') },
          { label: t('nav.loggers'), href: adminLoggersPath() },
          { label: t('admin.loggers.editTitle') },
        ]
      : [
          { label: t('nav.administration') },
          { label: t('nav.loggers'), href: adminLoggersPath() },
          { label: t('admin.loggers.newButton') },
        ],
  )

  const form = useForm({
    firstName: logger?.firstName ?? '',
    lastName: logger?.lastName ?? '',
    middleName: logger?.middleName ?? '',
    jobTitle: logger?.jobTitle ?? '',
    employeeId: logger?.employeeId ?? '',
    active: logger?.active ?? true,
  })

  const errors: FormErrors = (usePage<{ errors?: FormErrors }>().props.errors ?? {}) as FormErrors
  const canSubmit = useCan(Resources.ADMIN_LOGGER, isEdit ? Actions.UPDATE : Actions.CREATE)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({ logger: data }))
    if (isEdit && logger?.id) {
      form.patch(adminLoggerPath(logger.id))
    } else {
      form.post(adminLoggersPath())
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-6">
        <h1 className="text-2xl font-bold mb-6">
          {isEdit ? t('admin.loggers.editTitle') : t('admin.loggers.form.submitCreate')}
        </h1>

        {errors.base && (
          <div className="mb-4 rounded border border-destructive bg-destructive/10 px-4 py-3">
            {errors.base.map((message, index) => (
              <p key={index} className="text-sm text-destructive">
                {message}
              </p>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="firstName">{t('admin.loggers.form.firstName')} *</Label>
              <Input
                id="firstName"
                value={form.data.firstName}
                onChange={(e) => form.setData('firstName', e.target.value)}
                placeholder={t('profile.personalInfo.firstNamePlaceholder')}
                required
              />
              <FieldError errors={errors} field="first_name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName">{t('admin.loggers.form.lastName')} *</Label>
              <Input
                id="lastName"
                value={form.data.lastName}
                onChange={(e) => form.setData('lastName', e.target.value)}
                placeholder={t('profile.personalInfo.lastNamePlaceholder')}
                required
              />
              <FieldError errors={errors} field="last_name" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="middleName">{t('admin.loggers.form.middleName')}</Label>
            <Input
              id="middleName"
              value={form.data.middleName ?? ''}
              onChange={(e) => form.setData('middleName', e.target.value)}
              placeholder={t('profile.personalInfo.middleNamePlaceholder')}
            />
            <FieldError errors={errors} field="middle_name" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="jobTitle">{t('admin.loggers.form.jobTitle')}</Label>
            <Input
              id="jobTitle"
              value={form.data.jobTitle ?? ''}
              onChange={(e) => form.setData('jobTitle', e.target.value)}
              placeholder={t('profile.personalInfo.jobTitlePlaceholder')}
            />
            <FieldError errors={errors} field="job_title" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="employeeId">{t('admin.loggers.form.employeeId')}</Label>
            <Input
              id="employeeId"
              value={form.data.employeeId ?? ''}
              onChange={(e) => form.setData('employeeId', e.target.value)}
              placeholder={t('profile.personalInfo.employeeIdPlaceholder')}
            />
            <FieldError errors={errors} field="employee_id" />
          </div>

          {isEdit && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="active"
                checked={form.data.active}
                onCheckedChange={(checked) => form.setData('active', checked === true)}
              />
              <Label htmlFor="active" className="cursor-pointer">
                {t('admin.loggers.form.activeLabel')}
              </Label>
            </div>
          )}

          {!canSubmit && <NoAccessNotice />}

          <div className="flex gap-3 pt-2">
            {canSubmit && (
              <Button type="submit" disabled={form.processing}>
                {isEdit
                  ? t('admin.loggers.form.submitUpdate')
                  : t('admin.loggers.form.submitCreate')}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => router.visit(adminLoggersPath())}
            >
              {t('admin.loggers.form.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LoggerForm
